import { getApiUrl } from "@app/shared";
import { useSignalr } from "@known-as-bmf/react-signalr";
import { useEffect } from "react";
import { Subscription } from "rxjs";

// Keep in sync with AlertHub's methods in CoreApi.
const WATCH_COMMAND = "WatchAlerts";
const STOP_COMMAND = "StopWatchingAlerts";
const WATCH_ORG_COMMAND = "WatchOrganizationAlerts";
const STOP_ORG_COMMAND = "StopWatchingOrganizationAlerts";
// Keep in sync with IAlertClient in CoreApi.
const ALERT_CHANGED_EVENT = "AlertChanged";
const ALERTS_CHANGED_EVENT = "AlertsChanged";

type AlertsHubHandlers = {
    /** Rich per-user push — only fires for alerts matching an in-app subscription. Carries the payload. */
    onAlertChanged: (payload: unknown) => void;
    /**
     * Org-wide "state moved, re-read" signal. Fires for every alert transition in the organization
     * regardless of subscriptions, which is what keeps the bell in step with the tables after an
     * acknowledge, a resolve, or a background auto-resolve — including ones this user didn't perform.
     */
    onAlertsChanged: () => void;
};

// Subscribes the calling component to alert updates for a user and their organization. Payload shape
// for onAlertChanged matches AlertNotificationPayload on the server.
export function useAlertsHub(userId: string | undefined, organizationId: string | undefined, handlers: AlertsHubHandlers) {
    const { send, on } = useSignalr(getApiUrl("/hubs/alerts"));
    const { onAlertChanged, onAlertsChanged } = handlers;

    useEffect(() => {
        if (!userId) return;
        send(WATCH_COMMAND, userId);
        return () => {
            send(STOP_COMMAND, userId);
        };
    }, [userId, send]);

    useEffect(() => {
        // Re-joins on org switch, so a superadmin changing organizations stops hearing about the old
        // one instead of accumulating groups.
        if (!organizationId) return;
        send(WATCH_ORG_COMMAND, organizationId);
        return () => {
            send(STOP_ORG_COMMAND, organizationId);
        };
    }, [organizationId, send]);

    useEffect(() => {
        const subs: Subscription[] = [
            on<unknown>(ALERT_CHANGED_EVENT).subscribe((p) => onAlertChanged(p)),
            on<unknown>(ALERTS_CHANGED_EVENT).subscribe(() => onAlertsChanged()),
        ];
        return () => subs.forEach((s) => s.unsubscribe());
    }, [on, onAlertChanged, onAlertsChanged]);
}
